import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material';
import { LimitDialogComponent } from './components/limit-dialog/limit-dialog.component';
import { LegacyDialogComponent, LegacyDialogData, LegacyDialogEnum } from './components/legacy-dialog/legacy-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from './components/confirm-dialog/confirm-dialog.component';
import { SaveBookmarkDialogComponent, SaveBookmarkDialogData, SaveBookmarkDialogMode } from './components/save-bookmark-dialog/save-bookmark-dialog.component';
import { BookmarkModel } from '../bookmarks/models/bookmark.model';
import { AccessDialogData } from './components/access-dialog/access-dialog.component';
import { GroupRestrictionDialogData, GroupRestrictionDialogComponent } from './components/group-restriction-dialog/group-restriction-dialog.component';
import { ClipboardDialogComponent, ClipboardDialogData } from './components/clipboard-dialog/clipboard-dialog.component';
import { ScreenDimensions } from '../shared/models/screen.dimensions.model';
import { WelcomeTourDialogComponent, WelcomeTourDialogData, WelcomeTourEnum } from './components/welcome-tour-dialog/welcome-tour-dialog.component';
import { DomainJoinDialogData, DomainJoinDialogComponent } from './components/domain-join-dialog/domain-join-dialog.component';
import { OrganizationModel } from '../organizations/models/organization.model';
import { AboutDialogComponent } from './components/about-dialog/about-dialog.component';
import { SaveModuleDialogComponent } from './components/save-module-dialog/save-module-dialog.component';
import { NpsDialogComponent, NpsDialogData } from './components/nps-dialog/nps-dialog.component';
import { ImportModelDialogComponent } from './components/import-model-dialog/import-model-dialog.component';
import { AddTeamModelDialogComponent } from './components/add-team-model-dialog/add-team-model-dialog.component';
import { DialogIds } from './models/dialog-ids';
import { BackgroundColorDialogComponent } from './components/background-color-dialog/background-color-dialog.component';

const mobileScreenWidth = 768;

const transparentDialogCSSClass = 'backdropClass';

@Injectable({
  providedIn: 'root'
})
export class DialogService {

  get isMobile(): boolean {    
    return window.innerWidth <= mobileScreenWidth;
  }

  constructor(
    private dialog: MatDialog
  ) { }

  openClipboardDialog(copyText: string, config?: MatDialogConfig<ClipboardDialogData>) {
    const options = {      
      data: {
        id: DialogIds.ClipboardDialog,
        copyText: copyText
      },
      ...config
    };

    return this.dialog.open(ClipboardDialogComponent, options);
  }
  
  openWelcomeTourDialog(tourVersion: 'first_tour' | 'updated_tour', config?: MatDialogConfig<WelcomeTourDialogData>) {
    const userType: WelcomeTourEnum = tourVersion === 'updated_tour' ? WelcomeTourEnum.ExistingUser : WelcomeTourEnum.NewUser;

    const options: MatDialogConfig<WelcomeTourDialogData> = {
      id: DialogIds.WelcomeTourDialog,
      disableClose: true,
      data: {
        userType: userType
      },
      ...config
    };
    return this.dialog.open(WelcomeTourDialogComponent, options);
  }

  openJoinDomainDialog(organization: OrganizationModel, config?: MatDialogConfig<DomainJoinDialogData>) {
    const options: MatDialogConfig<DomainJoinDialogData> = {
      id: DialogIds.JoinDomainDialog,
      data: {
        organization: organization
      },
      ...config
    };
    return this.dialog.open(DomainJoinDialogComponent, options);
  }

  openNpsSurveyDialog(surveyGroupName: string, config?: MatDialogConfig<NpsDialogData>) {
    const options: MatDialogConfig<NpsDialogData> = {
      id: DialogIds.NpsSurveyDialog,
      data: {
        surveyGroupName: surveyGroupName
      },
      ...config
    };
    return this.dialog.open(NpsDialogComponent, options);
  }

  openConfirmDialog(config?: MatDialogConfig<ConfirmDialogData>) {
    const options = {
      id: DialogIds.ConfirmDialog,
      ...config
    };

    return this.dialog.open(ConfirmDialogComponent, options);
  }

  openLimitDialog() {
    this.dialog.closeAll();

    return this.dialog.open(LimitDialogComponent, {
      id: DialogIds.LimitDialog,
      width: '480px'
    });
  }

  openUpgradeDialog(config?: MatDialogConfig<LegacyDialogData>) {
    const dimensions: ScreenDimensions = {
      width: 925,
      height: 700
    };
    const options = {
      id: DialogIds.UpgradeDialog,
      ...{
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        data: <LegacyDialogData>{
          type: LegacyDialogEnum.UpgradeSubscription,
          dimensions: dimensions
        }
      },
      ...config
    };

    this.dialog.closeAll();
    return this.dialog.open(LegacyDialogComponent, options);
  }

  openBuyDialog(config?: MatDialogConfig<LegacyDialogData>) {
    const dimensions: ScreenDimensions = {
      width: 550,
      height: 650
    };
    const options = {
      id: DialogIds.BuyDialog,
      ...{
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        data: <LegacyDialogData>{
          type: LegacyDialogEnum.BuySubscription,
          dimensions: dimensions
        }
      },
      ...config
    };

    this.dialog.closeAll();
    return this.dialog.open(LegacyDialogComponent, options);
  }

  openProfileDialog(config?: MatDialogConfig<LegacyDialogData>, tab: string = '') {
    const dimensions: ScreenDimensions = {
      width: 750,
      height: 600
    };
    const options = {
      id: DialogIds.ProfileDialog,
      ...{
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        data: {
          type: LegacyDialogEnum.MyProfile,
          dimensions: dimensions,
          options: {
            show_tab: tab
          }
        }
      },
      ...config
    };

    this.dialog.closeAll();
    return this.dialog.open(LegacyDialogComponent, options);
  }

  openSaveModuleDialog(id: string, config?: MatDialogConfig<SaveBookmarkDialogData>) {
    this.dialog.closeAll();

    const options = {
      id: DialogIds.SaveModuleDialog,
      ...{
        width: '480px',
        data: {
          id: id
        }
      },
      ...config
    };

    return this.dialog.open(SaveModuleDialogComponent, options);
  }

  openSaveBookmarkDialog(bookmark: BookmarkModel, config?: MatDialogConfig<SaveBookmarkDialogData>) {
    return this._openSaveBookmarkDialog({
      id: DialogIds.SaveBookmarkDialog,
      data: {
        bookmark: bookmark,
        mode: SaveBookmarkDialogMode.SaveStory
      },
      ...config
    });
  }

  openSaveAsBookmarkDialog(title: string, bookmarkTitle: string, message?: string, config?: MatDialogConfig<SaveBookmarkDialogData>) {
    return this._openSaveBookmarkDialog({
      id: DialogIds.SaveBookmarkDialog,
      data: {
        title: title,
        bookmarkTitle: bookmarkTitle,
        message: message,
        mode: SaveBookmarkDialogMode.SaveStory
      },
      ...config
    });
  }

  openManageDialog(bookmark: BookmarkModel, config?: MatDialogConfig<SaveBookmarkDialogData>) {
    return this._openSaveBookmarkDialog({
      id: DialogIds.SaveBookmarkDialog,
      data: {
        bookmark: bookmark,
        mode: SaveBookmarkDialogMode.Manage
      },
      ...config
    });
  }

  openBookmarkAccessDialog(bookmark: BookmarkModel, config?: MatDialogConfig<AccessDialogData>) {
    const options = {
      id: DialogIds.BookmarkAccessDialog,
      width: '480px',
      data: {
        model: bookmark
      },
      ...config
    };

    this.dialog.closeAll();
    return this.dialog.open(SaveBookmarkDialogComponent, options);
  }

  openGroupRestrictionDialog(config?: MatDialogConfig<GroupRestrictionDialogData>) {
    const options = {
      id: DialogIds.GroupRestrictionDialog,
      ...{
        width: '480px',
        height: 'auto'
      },
      ...config
    };

    return this.dialog.open(GroupRestrictionDialogComponent, options);
  }

  openPremiumContentDialog() {
    const bodyText = this.isMobile
      ? 'This is premium content. Visit us on desktop to upgrade to a premium plan.'
      : 'This is premium content. Upgrade to a Premium Plan for unlimited access.';

    return this.openConfirmDialog({
      id: DialogIds.PremiumContentDialog,
      data: {
        titleText: 'Premium Content',
        bodyText: bodyText,
        confirmText: 'Go Premium',
        confirmEnabled: !this.isMobile,
        cancelText: 'OK',
        confirmColor: 'warn'
      }
    });
  }

  openAboutDialog() {
    this.dialog.closeAll();

    return this.dialog.open(AboutDialogComponent, {
      id: DialogIds.AboutDialog
    });
  }

  openImportDialog() {
    this.dialog.closeAll();

    const options = {
      id: DialogIds.ImportDialog,
      maxWidth: '90vw',
      width: '90vw',
      height: '90vh'
    };

    return this.dialog.open(ImportModelDialogComponent, options);
  }
  
  openAddTeamModelDialog(bookmark: BookmarkModel) {
    const options = {
      id: DialogIds.AddTeamModelDialog,
      data: {
        bookmark: bookmark
      }
    };
    return this.dialog.open(AddTeamModelDialogComponent, options);
  }

  openBackgroundColorDialog() {
    const options = {
      backdropClass: transparentDialogCSSClass,
      data: {
      }
    };
    return this.dialog.open(BackgroundColorDialogComponent, options);
  }

  private _openSaveBookmarkDialog(config?: MatDialogConfig<SaveBookmarkDialogData>) {
    const options = {
      mode: SaveBookmarkDialogMode.SaveStory,
      ...config
    };

    this.dialog.closeAll();
    return this.dialog.open(SaveBookmarkDialogComponent, options);
  }
}
